/**
 * THE DESCRIPTION, WITH ITS SHAPE ON — AND A SHOW MORE.
 *
 * The owner: "add a show more to the description." Underneath that request was
 * a worse problem: there was nothing worth revealing. A 1,462-character Hebrew
 * spec sheet arrived as one unbroken paragraph, because the importer's
 * converter could not see `<br data-start="391" />` and dropped his headings,
 * his bold, his list and his nineteen-row table at the same seam.
 *
 * So the server now stores the description twice — the plain string every
 * other reader has always had, and an allow-listed subset with the structure
 * kept — and this renders the second one.
 *
 * HOW IT RENDERS IT IS THE SECURITY ARGUMENT. `dangerouslySetInnerHTML` does
 * not appear in this file. `parseRichText` turns the subset into plain objects
 * and every element below is built by `React.createElement` from a type this
 * file recognises. There is no path from a merchant's byte to an attribute:
 * the parser never reads one, and the renderer never writes one it was given.
 *
 * The collapse is a CLIP, not a truncation. The whole description is in the
 * DOM from the first paint, so it is readable with JavaScript off, findable by
 * ⌘F, and indexable — and the button only changes how much of it you can see.
 */
import React, { useMemo, useState } from 'react';
import { parseRichText, richTextLength } from '../../utils/richText';
import styles from './styles.module.css';

/**
 * Above this many characters of plain text, the description gets a show-more.
 * Measured on the CONTENT rather than on a rendered height: a height needs
 * layout, which a test does not have and a phone and a desktop disagree about,
 * and this has to be the same answer everywhere.
 */
export const COLLAPSE_ABOVE_CHARS = 320;

/** A plain-text description, given the same paragraph shape as a rich one. */
export function blocksFromPlain(text) {
  return String(text || '')
    .split(/\n{1,}/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => ({ type: 'p', children: [{ type: 'text', text: line }] }));
}

const CELL = { td: 'td', th: 'th' };

/**
 * Exported so the warranty and returns panels draw the merchant's words with
 * the SAME renderer the description uses. Two renderers would be two
 * allow-lists, and the second one is always the one that grows an escape
 * hatch — `dangerouslySetInnerHTML` does not appear in this file and must not
 * appear in a sibling either.
 */
export function renderNodes(nodes, keyPrefix = 'n') {
  const out = [];
  (nodes || []).forEach((node, i) => {
    if (!node) return;
    const key = `${keyPrefix}-${i}`;
    switch (node.type) {
      case 'text':
        out.push(<React.Fragment key={key}>{node.text}</React.Fragment>);
        break;
      case 'br':
        out.push(<br key={key} />);
        break;
      case 'hr':
        out.push(<hr key={key} className={styles.richRule} />);
        break;
      case 'p':
        out.push(<p key={key} className={styles.richParagraph}>{renderNodes(node.children, key)}</p>);
        break;
      case 'h3':
        out.push(<h3 key={key} className={styles.richHeading}>{renderNodes(node.children, key)}</h3>);
        break;
      case 'h4':
        out.push(<h4 key={key} className={styles.richSubHeading}>{renderNodes(node.children, key)}</h4>);
        break;
      case 'strong':
        out.push(<strong key={key}>{renderNodes(node.children, key)}</strong>);
        break;
      case 'em':
        out.push(<em key={key}>{renderNodes(node.children, key)}</em>);
        break;
      case 'ul':
        out.push(<ul key={key} className={styles.richList}>{renderNodes(node.children, key)}</ul>);
        break;
      case 'ol':
        out.push(<ol key={key} className={styles.richList}>{renderNodes(node.children, key)}</ol>);
        break;
      case 'li':
        out.push(<li key={key}>{renderNodes(node.children, key)}</li>);
        break;
      case 'table':
        // A specification table is wide and the page is not: it scrolls inside
        // its own box rather than pushing the whole page sideways.
        out.push(
          <div key={key} className={styles.richTableWrap}>
            <table className={styles.richTable}>{renderNodes(node.children, key)}</table>
          </div>,
        );
        break;
      case 'thead':
        out.push(<thead key={key}>{renderNodes(node.children, key)}</thead>);
        break;
      case 'tbody':
        out.push(<tbody key={key}>{renderNodes(node.children, key)}</tbody>);
        break;
      case 'tr':
        out.push(<tr key={key}>{renderNodes(node.children, key)}</tr>);
        break;
      case 'td':
      case 'th':
        out.push(React.createElement(
          CELL[node.type],
          { key, className: styles.richCell },
          renderNodes(node.children, key),
        ));
        break;
      default:
        // A type this renderer does not know is not rendered. There is no
        // fall-through that turns an unknown node into markup.
        break;
    }
  });
  return out;
}

/**
 * A table that arrives without a `<tbody>` — which is what a re-serialised
 * subset produces when the merchant wrote none — still has to be legal HTML,
 * or React warns and the browser re-parents the rows.
 */
export function normaliseTables(nodes) {
  return (nodes || []).map((node) => {
    if (!node || !node.children) return node;
    if (node.type !== 'table') return { ...node, children: normaliseTables(node.children) };
    const rows = [];
    const sections = [];
    for (const child of node.children) {
      if (child.type === 'tr') rows.push(child);
      else sections.push({ ...child, children: normaliseTables(child.children) });
    }
    return { ...node, children: rows.length ? [...sections, { type: 'tbody', children: rows }] : sections };
  });
}

const RichDescription = ({ html, text, t, isRtl, id = 'product-description' }) => {
  const blocks = useMemo(() => {
    const parsed = normaliseTables(parseRichText(html));
    return parsed.length ? parsed : blocksFromPlain(text);
  }, [html, text]);

  const collapsible = useMemo(() => richTextLength(blocks) > COLLAPSE_ABOVE_CHARS, [blocks]);
  const [expanded, setExpanded] = useState(false);
  const clipped = collapsible && !expanded;

  if (!blocks.length) return null;

  return (
    <>
      <div
        id={id}
        dir={isRtl ? 'rtl' : 'ltr'}
        className={`${styles.richBody}${clipped ? ` ${styles.richBodyCollapsed}` : ''}`}
        data-testid="product-detail-description-body"
      >
        {renderNodes(blocks)}
      </div>
      {collapsible && (
        <button
          type="button"
          className={styles.showMoreBtn}
          aria-expanded={expanded ? 'true' : 'false'}
          aria-controls={id}
          onClick={() => setExpanded((v) => !v)}
          data-testid="product-detail-description-more"
        >
          {expanded
            ? t('productDetail.showLess', { defaultValue: 'Show less' })
            : t('productDetail.showMore', { defaultValue: 'Show more' })}
        </button>
      )}
    </>
  );
};

export default RichDescription;
